import React, { useMemo, useState } from 'react';

import { GetFilterBounds_deal_performance_summary } from 'features/core/filter/__generated__/GetFilterBounds';
import { UseCarveFunctions } from 'features/deals/DealStages/EventActionCards/CarveDetailsCardBody/hooks';
import {
  CarveAction,
  CarveSpecification,
} from 'features/deals/DealStages/EventActionCards/CarveDetailsCardBody/types';
import { CarveSummary } from 'features/deals/DealStages/EventActionCards/CarveDiffTable/__generated__/CarveSummary';
import { Button, CarveActions, CarveCardContainer, CarveName, Subheader, Spinner } from 'ui-kit';

import { GET_DEAL_GENERAL_INFO_QUERY } from 'features/deals/dealCards/gql/dealDetailsQueries';
import { GET_DEAL_TIMELINE } from 'features/deals/DealStages/fragments';

import { GetCarvesVariables } from 'query/__generated__/GetCarves';
import { GET_CARVES } from 'query/getCarves';

import { CarveCard, CarveDetailsTable } from './CarveCard';
import { CreateCarveForm, Criteria } from './CreateCarveForm';
import CreateCarveQuestion from './CreateCarveQuestion';
import { criteriaToFilters } from './criteriaToFilterMapper';
import { useDealContext } from '../BiddingAndPricing/hooks/useDealContext';

interface TradeCarveManagerProps {
  dealId: string;
  carveSpecifications: CarveSpecification[];
  originalOffer: CarveSummary | null;
  filterBounds: GetFilterBounds_deal_performance_summary | undefined;
  onCarveAction: (action: CarveAction) => void;
  carveFunctions: UseCarveFunctions;
  onNoCarve: () => void;
}

const TradeCarveManager: React.FC<TradeCarveManagerProps> = ({
  dealId,
  carveSpecifications,
  originalOffer,
  filterBounds,
  onCarveAction,
  onNoCarve,
  carveFunctions,
}) => {
  const { offeringId, buyerCompanyId } = useDealContext(dealId);

  const [isInCreateCarvesMode, setIsInCreateCarvesMode] = useState<boolean>(
    carveSpecifications != null && carveSpecifications.length > 0,
  );

  const [selectedCarveId, setSelectedCarveId] = useState<string>();

  const selectedCarve = useMemo(
    () => carveSpecifications.find((carve) => carve.id === selectedCarveId),
    [carveSpecifications, selectedCarveId],
  );

  const { deleteDealCarve, deleteLoading, createDealCarve, createLoading } = carveFunctions;

  const handleCreateNewCarve = () => {
    const newId = '' + Date.now();
    onCarveAction({
      type: 'create-draft-carve',
      id: newId,
      name: '',
      criteria: [],
    });
    setSelectedCarveId(newId);
  };

  const getCarvesVariables: GetCarvesVariables = useMemo(
    () => ({
      deal_id: dealId,
    }),
    [dealId],
  );

  const handleSaveCarve = async (carveName: string, criteria: Criteria[]) => {
    const filters = criteriaToFilters(criteria);
    await createDealCarve({
      variables: {
        input: {
          deal_id: dealId,
          name: carveName,
          carve: filters,
        },
      },
      refetchQueries: [
        {
          query: GET_CARVES,
          variables: getCarvesVariables,
        },
        {
          query: GET_DEAL_TIMELINE,
          variables: { id: dealId },
        },
        {
          query: GET_DEAL_GENERAL_INFO_QUERY,
          variables: { id: dealId },
        },
      ],
      onCompleted: ({ createDealCarve }) => {
        onCarveAction({
          type: 'save-carve',
          id: selectedCarve?.id || '',
          name: carveName,
          newId: createDealCarve.id,
        });
        setSelectedCarveId(createDealCarve.id);
      },
    });
  };

  const handleCriteriaChanged = (criteria: Criteria[]) => {
    if (selectedCarve === undefined) return;
    onCarveAction({
      type: 'update-carve',
      carveSpec: { ...selectedCarve, criteria },
    });
  };

  const handleRemoveCarve = async (carveId: string | undefined) => {
    if (carveId === undefined) return;

    if (!offeringId || !buyerCompanyId) {
      console.error('Missing offeringId or buyerCompanyId for carve deletion');
      return;
    }

    // Clear selection since the carve is being removed
    setSelectedCarveId(undefined);

    // Use the proper delete logic that handles optimistic UI updates
    onCarveAction({
      type: 'delete-carve',
      id: carveId,
    });

    try {
      await deleteDealCarve({
        variables: {
          id: carveId,
          offeringId: offeringId,
          buyerCompanyId: buyerCompanyId,
        },
        refetchQueries: [
          {
            query: GET_CARVES,
            variables: getCarvesVariables,
          },
          {
            query: GET_DEAL_TIMELINE,
            variables: { id: dealId },
          },
          {
            query: GET_DEAL_GENERAL_INFO_QUERY,
            variables: { id: dealId },
          },
        ],
      });
    } catch (error) {
      // If deletion fails, we should handle the error
      // The refetchQueries will restore the correct state
      console.error('Failed to delete carve:', error);
    }
  };

  if (originalOffer === null) {
    return null;
  }

  const onClickToNextCard = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onNoCarve();
  };

  const canClickNext =
    carveSpecifications != null &&
    carveSpecifications.length > 0 &&
    carveSpecifications.some((carve) => !carve.isDraft);

  return (
    <div className="text-slate-200">
      <Subheader>
        Create and save pool carves from the offering. Changes to selected carves auto-save. Select
        saved carves for pricing.
      </Subheader>
      {isInCreateCarvesMode ? (
        <>
          <div className="mt-4 flex flex-nowrap gap-x-3">
            <CreateCarveForm
              selectedCarve={selectedCarve}
              filterBounds={filterBounds}
              onSaveCarve={handleSaveCarve}
              disabled={!selectedCarve || createLoading}
              onCriteriaChanged={handleCriteriaChanged}
              isUpdating={selectedCarve?.isUpdating || false}
            />
            <div className="max-w-[calc(100%-400px)]">
              <CarveActions>
                <Button onClick={handleCreateNewCarve} label={'create new carve'}>
                  Create New Carve
                </Button>
                <Button
                  type="secondary"
                  disabled={selectedCarveId === undefined || deleteLoading}
                  onClick={() => handleRemoveCarve(selectedCarveId)}
                  label={'Remove carve'}
                >
                  {deleteLoading ? (
                    <div className="flex items-center gap-2">
                      <Spinner loading={true} size={16} color="#f093ee" />
                      <span>Removing...</span>
                    </div>
                  ) : (
                    'Remove Carve'
                  )}
                </Button>
              </CarveActions>
              <div className="flex w-full flex-wrap items-start gap-2">
                <CarveCardContainer highlight={false}>
                  <CarveName>Original offer</CarveName>
                  <CarveDetailsTable carveSummary={originalOffer} />
                </CarveCardContainer>

                {carveSpecifications
                  ?.filter((carve) => !carve.isDeleted)
                  .map((carve) => {
                    const isSelected = carve.id === selectedCarveId;
                    return (
                      <CarveCardContainer highlight={isSelected} key={carve.id}>
                        <CarveCard
                          carve={carve}
                          isSelected={isSelected}
                          onCarveSelected={() => setSelectedCarveId(carve.id)}
                        />
                      </CarveCardContainer>
                    );
                  })}
              </div>
            </div>
          </div>
          <Button
            label={'Next'}
            disabled={!canClickNext}
            onClick={onClickToNextCard}
            type="primary"
          >
            Next
          </Button>
        </>
      ) : (
        <CreateCarveQuestion
          setCreateCarve={setIsInCreateCarvesMode}
          onNoCarve={onClickToNextCard}
        />
      )}
    </div>
  );
};

export type { TradeCarveManagerProps };

export { TradeCarveManager };
